/**
 * A WALL-CLOCK TIME IN A NAMED TIME ZONE, AND THE EXACT INSTANT IT IS - the arithmetic under
 * `DateTimeField` (design 24, T29).
 *
 * ★ WHY NOT THE PICKER'S OWN ZONE. `@mantine/dates` 9 hands back a wall time with no zone at all,
 * `YYYY-MM-DD HH:mm:ss`, and its `dayjs` reads such a string in the BROWSER's zone. The field's time
 * is the author's - their Primary location's zone, or the browser's when there is none - and the
 * server stores the instant (`plannedGoLiveAt`). So the wall time is turned into an instant here, in
 * the zone the field names, never by `new Date(wall)`: 18:30 on 20 Oct 2026 in Europe/Minsk is
 * 2026-10-20T15:30:00Z whatever zone the browser is in.
 *
 * ★ NO CLOCK IS READ. Every function takes the instant or the wall time it works on (C11:
 * `eslint.config.mjs`, "THE CLOCK RULE"); `new Date(ms)` below parses a number it was given.
 *
 * ★ THE ZONE'S OFFSET COMES FROM `Intl`, which knows the IANA database, so a summer-time change is
 * the zone's own. A wall time that happens twice (the hour a clock falls back through) is the
 * earlier of the two; one that does not exist (the hour a clock springs over) is moved on by the
 * size of the jump - 02:30 on a spring-forward night is 03:30 - as browsers and `Temporal` do.
 */

/** `YYYY-MM-DD HH:mm:ss`, the shape `@mantine/dates` 9 reads and writes. */
export type WallTime = string;

const WALL = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/u;

/** Is `zone` an IANA zone this browser knows? An unknown one would make every `Intl` call throw. */
export function isKnownTimeZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** The zone's wall clock at `epochMs`, as the numbers `Date.UTC` takes. */
function wallParts(
  epochMs: number,
  zone: string,
): readonly [number, number, number, number, number, number] {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(epochMs));
  const part = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((candidate) => candidate.type === type)?.value ?? '0');
  return [part('year'), part('month'), part('day'), part('hour'), part('minute'), part('second')];
}

/** How far the zone's wall clock is ahead of UTC at `epochMs`, in milliseconds. */
function offsetMs(epochMs: number, zone: string): number {
  const [year, month, day, hour, minute, second] = wallParts(epochMs, zone);
  const wholeSecond = Math.floor(epochMs / 1000) * 1000;
  return Date.UTC(year, month - 1, day, hour, minute, second) - wholeSecond;
}

const pad = (value: number, width = 2): string => String(value).padStart(width, '0');

const DAY_MS = 24 * 60 * 60 * 1000;

/** `2026-10-20T15:30:00Z`: the instant to the second, as the wire takes it. */
function isoSecond(epochMs: number): string {
  return new Date(epochMs).toISOString().replace(/\.\d{3}Z$/u, 'Z');
}

/**
 * The instant a wall time in `zone` is, as `YYYY-MM-DDTHH:mm:ssZ` - or `null` for a string that is
 * not a wall time. A date alone is that day's midnight.
 */
export function wallToInstant(wall: WallTime, zone: string): string | null {
  const match = WALL.exec(wall.trim());
  if (match === null) {
    return null;
  }
  const [, year, month, day, hour = '0', minute = '0', second = '0'] = match;
  const wanted = [year, month, day, hour, minute, second].map(Number);
  const asUtc = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
  );
  // The zone's offset a day either side: the two a summer-time change can be between (a zone never
  // changes twice in a day). Each gives a candidate; a candidate counts if it shows that wall time.
  const before = asUtc - offsetMs(asUtc - DAY_MS, zone);
  const after = asUtc - offsetMs(asUtc + DAY_MS, zone);
  const shows = (epochMs: number): boolean =>
    wallParts(epochMs, zone).every((value, index) => value === wanted[index]);
  const valid = [before, after].filter(shows);
  // Twice: the earlier. Never (the gap): the offset from before the jump, which lands after it.
  return isoSecond(valid.length === 0 ? before : Math.min(...valid));
}

/** The wall time in `zone` that `instant` is, as `YYYY-MM-DD HH:mm:ss` - `null` if unparseable. */
export function instantToWall(instant: string, zone: string): WallTime | null {
  const epochMs = new Date(instant).getTime();
  if (Number.isNaN(epochMs)) {
    return null;
  }
  const [year, month, day, hour, minute, second] = wallParts(epochMs, zone);
  return `${pad(year, 4)}-${pad(month)}-${pad(day)} ${pad(hour)}:${pad(minute)}:${pad(second)}`;
}
